import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Bold, Italic, Strikethrough, Link, Smile, X } from "lucide-react";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import "./comment-editor.scss";
import { uploadFile } from "../../../api/upload";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";

export interface CommentAuthor {
  id: string;
  name: string;
}

interface CommentEditorProps {
  onSubmit: (html: string, attachments: string[]) => void;
  replyingTo: CommentAuthor | null;
  onCancelReply: () => void;
}

/**
 * CommentEditor provides a lightweight rich text editor for comments with
 * basic formatting, emoji insertion, link dialog, and file attachments.
 * Exposes a ref to focus the editable div from parent.
 */
const CommentEditor = forwardRef<HTMLDivElement, CommentEditorProps>(
  ({ onSubmit, replyingTo, onCancelReply }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const emojiPickerRef = useRef<HTMLDivElement>(null);
    const selectionRangeRef = useRef<Range | null>(null);
    useImperativeHandle(ref, () => editorRef.current as HTMLDivElement);

    const [html, setHtml] = useState("");
    const [attachments, setAttachments] = useState<string[]>([]);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
    const [typeFormat, setTypeFormat] = useState("");
    const [linkInput, setLinkInput] = useState("");
    const [linkText, setLinkText] = useState("");

    const checkFormattingState = (format: string): boolean => {
      const selection = globalThis.getSelection();
      if (!selection || selection.rangeCount === 0) return false;
      const range = selection.getRangeAt(0);
      let node: any = range.commonAncestorContainer;
      if (node.nodeType === Node.TEXT_NODE) {
        node = node.parentNode as Node;
      }
      while (node && node !== editorRef.current) {
        const element = node as HTMLElement;
        if (format === "bold" && element.tagName === "STRONG") return true;
        if (format === "italic" && element.tagName === "EM") return true;
        if (format === "strikethrough" && element.tagName === "S") return true;
        node = node.parentNode;
      }
      return false;
    };

    useEffect(() => {
      const handleSelectionChange = () => {
        if (document.activeElement === editorRef.current) {
          setIsBold(checkFormattingState("bold"));
          setIsItalic(checkFormattingState("italic"));
          setIsStrikethrough(checkFormattingState("strikethrough"));
          const selection = globalThis.getSelection();
          if (selection && selection.rangeCount > 0) {
            selectionRangeRef.current = selection.getRangeAt(0);
          }
        }
      };

      document.addEventListener("selectionchange", handleSelectionChange);
      return () =>
        document.removeEventListener("selectionchange", handleSelectionChange);
    }, []);

    const emojiToggleBtnRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          emojiPickerRef.current &&
          !emojiPickerRef.current.contains(target) &&
          emojiToggleBtnRef.current &&
          !emojiToggleBtnRef.current.contains(target)
        ) {
          setShowEmojiPicker(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const ensureEditorHasFocusAndContent = () => {
      const editor = editorRef.current;
      if (!editor) return;

      if (editor.innerHTML.trim() === "" || editor.innerHTML === "<br>") {
        editor.innerHTML = "<br>";
        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        const selection = globalThis.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
      editor.focus();
    };

    const applyFormat = (format: "bold" | "italic" | "strikethrough") => {
      ensureEditorHasFocusAndContent();
      // @ts-ignore - execCommand is deprecated but necessary for contentEditable
      document.execCommand(format, false);// NOSONAR
      editorRef.current?.focus();
      setIsBold(checkFormattingState("bold"));
      setIsItalic(checkFormattingState("italic"));
      setIsStrikethrough(checkFormattingState("strikethrough"));
      setTypeFormat(format);
    };

    const applyLink = () => {
      ensureEditorHasFocusAndContent();
      const selection = globalThis.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        selectionRangeRef.current = range;
        setLinkText(range.toString());
      } else {
        setLinkText("");
      }
      setLinkInput(""); // reset
      setIsLinkDialogOpen(true);
    };

    const handleInsertLink = () => {
      const editor = editorRef.current;
      if (!editor) return;

      const selection = globalThis.getSelection();
      if (!selection || !selectionRangeRef.current) return;
      if (selectionRangeRef.current && selection) {
        selection.removeAllRanges();
        selection.addRange(selectionRangeRef.current);
      }

      const range = selection?.getRangeAt(0);
      if (range) {
        const finalUrl = `https://${linkInput.replace(/^https?:\/\//, "")}`;
        const displayText = linkText.trim() ? linkText : finalUrl;
        if (range.collapsed) {
          const a = document.createElement("a");
          a.href = finalUrl;
          a.textContent = displayText;
          a.target = "_blank";
          range.insertNode(a);
          range.setStartAfter(a);
          range.setEndAfter(a);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          // @ts-ignore - execCommand is deprecated but necessary for contentEditable
          document.execCommand(// NOSONAR
            "insertHTML",
            false,
            `<a href="${finalUrl}" target="_blank">${displayText}</a>`,
          );
        }
        setHtml(editor.innerHTML);
      }

      setIsLinkDialogOpen(false);
      setLinkInput("");
      setLinkText("");
      editor.focus();
    };

    const toggleEmojiPicker = () => {
      if (!showEmojiPicker) {
        const selection = globalThis.getSelection();
        if (selection && selection.rangeCount > 0) {
          selectionRangeRef.current = selection.getRangeAt(0);
        }
      }
      setShowEmojiPicker((prev) => !prev);
    };

    const getPlainTextLength = () => {
      const editor = editorRef.current;
      return editor ? editor.innerText.length : 0;
    };

    const handleEmojiClick = (emojiData: EmojiClickData) => {
      const editor = editorRef.current;
      if (!editor) return;

      const selection = globalThis.getSelection();
      if (!selection || !selectionRangeRef.current) return;

      const range = selectionRangeRef.current;

      const rect = range.getBoundingClientRect();
      const caretY = rect.top;

      const prevScroll = editor.scrollTop;

      selection.removeAllRanges();
      selection.addRange(range);

      const currentLength = editor.innerText.length;
      if (currentLength >= 1000) return;

      range.deleteContents();
      const emojiNode = document.createTextNode(emojiData.emoji);
      range.insertNode(emojiNode);

      range.setStartAfter(emojiNode);
      range.setEndAfter(emojiNode);
      selection.removeAllRanges();
      selection.addRange(range);

      setHtml(editor.innerHTML);

      requestAnimationFrame(() => {
        const newRect = range.getBoundingClientRect();
        const deltaY = newRect.top - caretY;
        editor.scrollTop = prevScroll + deltaY;
      });

      setShowEmojiPicker(false);
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        const uploadedUrl = await uploadFile(file);
        setAttachments((prev) => [...prev, uploadedUrl]);
      }
    };

    const handleInput = (e: any) => {   
      const value = e.currentTarget.innerHTML;
      setHtml(value);

      if (e.currentTarget.innerText.trim() === "") {
        e.currentTarget.innerHTML = "";
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      const selection = globalThis.getSelection();
      if (!selection) return;
      const range = selection.getRangeAt(0);
      const selectedText = range.toString();
      const currentTextLength =
        (e.currentTarget.innerText?.length || 0) - selectedText.length;
      const remainingLength = 1000 - currentTextLength;

      if (remainingLength > 0) {
        // @ts-ignore - execCommand is deprecated but necessary for contentEditable
        document.execCommand(// NOSONAR
          "insertText",
          false,
          text.slice(0, remainingLength),
        );
      }
    };

    const setCaretToEnd = (el: HTMLElement) => {
  const range = document.createRange();
  const sel = globalThis.getSelection();

  range.selectNodeContents(el);
  range.collapse(false); // move to end

  sel?.removeAllRanges();
  sel?.addRange(range);
};

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      }
      if(e?.key === " "){
        const el = e.currentTarget;
        el.innerHTML= `${html}` + " ";
        setHtml(`${html}` + " ");
        setCaretToEnd(el);
        setTimeout(() => {
          applyFormat(typeFormat as  "bold" | "italic" | "strikethrough"); ;
        }, 100);
      }

      const selection = globalThis.getSelection();
      if (!selection) return;
      const range = selection.getRangeAt(0);
      const selectedText = range.toString();

      if (
        (e.currentTarget.innerText?.length || 0) - selectedText.length >=
          1000 &&
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        e.key !== " "
      ) {
        e.preventDefault();
      }
      if (
        (e.currentTarget.innerText?.length || 0) - selectedText.length >=
          1000 &&
          e.key !== " " &&
        e.key === "Enter"
      ) {
        e.preventDefault();
      }
    };

    const handleSubmit = () => {
      if (editorRef?.current?.innerText?.trim()) {
        onSubmit(html, attachments);
        setHtml("");
        setAttachments([]);
        editorRef.current.innerHTML = "";
      }
    };

    return (
      <>
        <div className="comment-editor-wrapper">
          {replyingTo && (
            <div className="comment-editor__reply">
              <span>
                Replying to <strong>{replyingTo.name}</strong>
              </span>
              <button
                type="button"
                className="comment-editor__cancel-btn"
                onClick={onCancelReply}
              >
                <X size={16} />
                <span className="sr-only">Cancel reply</span>
              </button>
            </div>
          )}

          <div className="comment-editor">
            {/*NOSONAR */ /* eslint-disable-next-line*/}
            <div
              ref={editorRef}
              contentEditable
              role="textbox"
              tabIndex={0}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              className="comment-editor__input"
              aria-label="Comment editor"
              data-placeholder="Type your comment here"
            />
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <div className="comment-editor__attachments">
              {attachments.map((attachment, index) => (
                <div key={index + 1} className="comment-editor__attachment">
                  {attachment}
                </div>
              ))}
            </div>
            <div className="comment-editor__footer">
              <div className="comment-editor__toolbar">
                <button
                  type="button"
                  className={`comment-editor__icon-btn ${isBold ? "active" : ""}`}
                  onClick={() => applyFormat("bold")}
                  aria-pressed={isBold}
                >
                  <Bold size={20} />
                </button>
                <button
                  type="button"
                  className={`comment-editor__icon-btn ${isItalic ? "active" : ""}`}
                  onClick={() => applyFormat("italic")}
                  aria-pressed={isItalic}
                >
                  <Italic size={20} />
                </button>
                <button
                  type="button"
                  className={`comment-editor__icon-btn ${
                    isStrikethrough ? "active" : ""
                  }`}
                  onClick={() => applyFormat("strikethrough")}
                  aria-pressed={isStrikethrough}
                >
                  <Strikethrough size={20} />
                </button>
                <button
                  type="button"
                  className="comment-editor__icon-btn"
                  onClick={applyLink}
                >
                  <Link size={20} />
                </button>
                <div
                  className="comment-editor__emoji-wrapper"
                  ref={emojiPickerRef}
                >
                  <button
                    ref={emojiToggleBtnRef}
                    type="button"
                    className="comment-editor__icon-btn"
                    onClick={toggleEmojiPicker}
                  >
                    <Smile size={20} />
                  </button>
                </div>
                <button
                  type="button"
                  className="comment-editor__send-btn"
                  onClick={handleSubmit}
                  disabled={!html.trim() || getPlainTextLength() > 1000}
                >
                  Send
                </button>
              </div>
            </div>
            {showEmojiPicker && (
              <div
                className="comment-editor__emoji-picker"
                ref={emojiPickerRef}
              >
                <EmojiPicker onEmojiClick={handleEmojiClick} />
              </div>
            )}
          </div>
        </div>
        <Dialog
          open={isLinkDialogOpen}
          onClose={(event, reason) => {
            if (reason === "backdropClick") {
              return;
            }
            setIsLinkDialogOpen(false);
          }}
          slotProps={{
            paper: {
              style: {
                borderRadius: 16,
                overflow: "hidden",
                boxShadow:
                  "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
                width: 550,
                height: "auto",
              },
            },
          }}
        >
          <DialogTitle
            style={{
              fontFamily: "Manrope, sans-serif",
              fontSize: 18,
              fontStyle: "semi-bold",
              fontWeight: 600,
              color: "#403c6f",
              padding: "24px",
              marginBottom: 16,
            }}
          >
            Add a Link
          </DialogTitle>
          <DialogContent
            style={{
              padding: "24px !important",
              fontFamily: "Manrope, sans-serif",
              color: "#403c6f",
              fontSize: 14,
            }}
          >
            <div
              style={{
                fontFamily: "Manrope, sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: "#403c6f",
                marginBottom: 4,
              }}
            >
              Display Text
            </div>
            <TextField
              fullWidth
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              variant="outlined"
              placeholder="Enter the text to display"
              style={{
                marginBottom: 20,
              }}
              slotProps={{
                input: {
                  style: {
                    fontFamily: "Manrope, sans-serif",
                    fontSize: 14,
                    color: "#403c6f",
                    borderRadius: 8,
                    outline: "none",
                  },
                },
              }}
            />
            <div
              style={{
                fontFamily: "Manrope, sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: "#403c6f",
                marginBottom: 4,
              }}
            >
              Enter URL
            </div>
            <TextField
              fullWidth
              value={linkInput}
              onChange={(e) =>
                setLinkInput(e.target.value.replace(/^https?:\/\//, ""))
              }
              placeholder="example.com"
              variant="outlined"
              slotProps={{
                input: {
                  startAdornment: (
                    <span
                      style={{
                        color: "#403c6f",
                        marginRight: 4,
                        fontFamily: "Manrope, sans-serif",
                        fontSize: 14,
                      }}
                    >
                      https://
                    </span>
                  ),
                  style: {
                    fontFamily: "Manrope, sans-serif",
                    fontSize: 14,
                    color: "#403c6f",
                    borderRadius: 8,
                    outline: "none",
                  },
                },
              }}
              style={{
                marginBottom: 24,
              }}
            />
          </DialogContent>
          <DialogActions
            style={{
              padding: "12px 24px 16px",
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={() => setIsLinkDialogOpen(false)}
              style={{
                background: "transparent",
                color: "#403c6f",
                border: "1px solid #403c6f",
                borderRadius: 24,
                textTransform: "none",
                fontFamily: "Manrope, sans-serif",
                fontWeight: 600,
                fontStyle: "semi-bold",
                padding: "6px 12px",
                width: "105px",
                fontSize: 16,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInsertLink}
              disabled={!linkInput.trim() || !linkText.trim()}
              style={{
                backgroundColor: "#403c6f",
                color: "#FFFFFF",
                borderRadius: 24,
                textTransform: "none",
                fontFamily: "Manrope, sans-serif",
                fontWeight: 600,
                fontStyle: "semi-bold",
                padding: "8px 14px",
                width: "105px",
                fontSize: 16,
                opacity: !linkInput.trim() || !linkText.trim() ? 0.6 : 1,
              }}
            >
              Insert
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  },
);

CommentEditor.displayName = "CommentEditor";

export default CommentEditor;
